import { Option } from "@inrev/core";
import type { PDFDocumentProxy } from "pdfjs-dist";
import type {
	EventBus,
	PDFFindController,
	PDFLinkService,
	PDFPageView,
	PDFViewer,
} from "pdfjs-dist/web/pdf_viewer.mjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { clamp } from "../../lib/utils";
import {
	autoScrollTarget,
	type ContentBox,
	prefersReducedMotion,
	SCROLL_SETTLE_MS,
	topThirdTarget,
} from "./autoScroll";
import { type PageBox, type PdfRect, toPageBox, unionBox } from "./highlights";
import { createLayerStore, type PageLayer, type PageLayers } from "./layerStore";
import { loadPdfjs, type Pdfjs } from "./pdfjs";
import { printDocument } from "./print";
import {
	DEFAULT_SESSION,
	type Rotation,
	toRotation,
	type ViewerSession,
	type ZoomMode,
} from "./viewerSession";
import { isFitMode, nextZoomStep, toScaleValue, toZoomMode } from "./zoom";

export type DocumentLoadState =
	| { status: "idle" }
	| { status: "loading" }
	| { status: "password"; incorrect: boolean; checking: boolean }
	| { status: "ready" }
	| { status: "unreadable" }
	| { status: "failed" };

export type FindStatus = { current: number; total: number; pending: boolean };

type Engine = Pdfjs & {
	eventBus: EventBus;
	linkService: PDFLinkService;
	findController: PDFFindController;
	viewer: PDFViewer;
};

type PendingScroll = {
	page: number;
	rects: PdfRect[];
	onScrolled: (moved: boolean) => void;
	/** Where `place` sent the scroll, so a refine only moves when the page's real size shifts it. */
	placedTop?: number;
};

type ScaleChangingEvent = { scale: number; presetValue?: unknown };
type MatchesCount = { current: number; total: number };

/**
 * The page whose top has crossed this share of the viewport's height is the current one. A
 * short last page never crosses it, so scrolled to the bottom, the last page is current.
 */
const CURRENT_PAGE_LINE = 0.4;
const BOTTOM_TOLERANCE_PX = 1;
/** A refine after the page renders only scrolls when the target moved more than this. */
const REFINE_TOLERANCE_PX = 2;
const NO_FIND: FindStatus = { current: 0, total: 0, pending: false };
const HIGHLIGHT_HOST_CLASS = "documentViewerHighlights";

const pageViewOf = (viewer: PDFViewer, page: number): Option<PDFPageView> =>
	viewer.getPageView(page - 1);

const createEngine = (
	pdfjs: Pdfjs,
	container: HTMLDivElement,
	pagesRoot: HTMLDivElement,
): Engine => {
	const { components, core } = pdfjs;
	const eventBus = new components.EventBus();
	const linkService = new components.PDFLinkService({
		eventBus,
		externalLinkTarget: components.LinkTarget.BLANK,
	});
	const findController = new components.PDFFindController({ eventBus, linkService });
	const viewer = new components.PDFViewer({
		container,
		viewer: pagesRoot,
		eventBus,
		linkService,
		findController,
		annotationMode: core.AnnotationMode.ENABLE,
		annotationEditorMode: core.AnnotationEditorType.DISABLE,
	});
	linkService.setViewer(viewer);
	return { ...pdfjs, eventBus, linkService, findController, viewer };
};

/**
 * Clearing the document cancels every page's rendering, which `PDFViewer.cleanup` skips for
 * finished pages, and removes the listeners pdf.js added to `document` for it.
 */
const releaseDocument = ({ viewer, linkService }: Pick<Engine, "viewer" | "linkService">) => {
	viewer.setDocument(null);
	linkService.setDocument(null);
};

/**
 * The last page whose top is at or above `line`, by binary search over the pages' tops.
 * `topOf` gives none for a page with no view yet, which ends the search.
 */
export const pageAtLine = (
	line: number,
	pageCount: number,
	topOf: (page: number) => Option<number>,
): number => {
	let current = 1;
	let low = 1;
	let high = pageCount;
	while (low <= high) {
		const middle = Math.floor((low + high) / 2);
		const top = topOf(middle);
		if (Option.isNone(top)) break;
		if (top > line) high = middle - 1;
		else {
			current = middle;
			low = middle + 1;
		}
	}
	return current;
};

/** The page the reader is on, judged by where the scroll sits. */
const currentPageOf = (viewer: PDFViewer, container: HTMLElement): number => {
	const atBottom =
		container.scrollTop + container.clientHeight >= container.scrollHeight - BOTTOM_TOLERANCE_PX;
	if (atBottom && container.scrollTop > 0) return Math.max(viewer.pagesCount, 1);
	const line = container.scrollTop + container.clientHeight * CURRENT_PAGE_LINE;
	return pageAtLine(line, viewer.pagesCount, (page) => pageViewOf(viewer, page)?.div.offsetTop);
};

/** Puts each page's highlight host in its page div, creating it the first time. */
const hostLayers = (
	viewer: PDFViewer,
	hosts: Map<number, HTMLDivElement>,
	pageNumbers: readonly number[],
): PageLayers => {
	const attached = new Map<number, PageLayer>();
	for (const pageNumber of pageNumbers) {
		const view = pageViewOf(viewer, pageNumber);
		if (!view) continue;
		let host = hosts.get(pageNumber);
		if (!host) {
			host = document.createElement("div");
			host.className = HIGHLIGHT_HOST_CLASS;
			hosts.set(pageNumber, host);
		}
		if (host.parentElement !== view.div) view.div.append(host);
		const { viewBox, rotation } = view.viewport;
		attached.set(pageNumber, { host, viewBox, rotation });
	}
	return attached;
};

const contentBoxOf = (container: HTMLElement, pageDiv: HTMLElement, box: PageBox): ContentBox => {
	const origin = container.getBoundingClientRect();
	const pageRect = pageDiv.getBoundingClientRect();
	const left = pageRect.left - origin.left + container.scrollLeft + pageDiv.clientLeft;
	const top = pageRect.top - origin.top + container.scrollTop + pageDiv.clientTop;
	return {
		left: left + box.left * pageDiv.clientWidth,
		top: top + box.top * pageDiv.clientHeight,
		width: box.width * pageDiv.clientWidth,
		height: box.height * pageDiv.clientHeight,
	};
};

type UseDocumentViewerOptions = {
	url: Option<string>;
	initialSession?: Partial<ViewerSession>;
	onDocumentLoad?: (pdf: PDFDocumentProxy) => void;
	/**
	 * Fetches a fresh URL, resolving it, or none when the fetch fails. A failed load calls it once
	 * before giving up, whatever the error: GCS answers an expired signed URL with a 400.
	 */
	refreshUrl: () => Promise<Option<string>>;
};

/**
 * Owns one pdf.js viewer for as long as the component is mounted: creates it, loads documents
 * into it, mirrors its page, zoom, rotation and find state, and keeps a highlight host attached
 * to each rendered page. Page tracking runs off pdf.js events, so scrolling causes no React
 * render unless the current page changes, and highlight hosts live in a store outside React
 * state, so drawing a page doesn't render the viewer either.
 */
export const useDocumentViewer = ({
	url,
	initialSession,
	onDocumentLoad,
	refreshUrl,
}: UseDocumentViewerOptions) => {
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const [pagesRoot, setPagesRoot] = useState<HTMLDivElement | null>(null);
	const [engine, setEngine] = useState<Option<Engine>>(undefined);
	const [attempt, setAttempt] = useState(0);
	const [engineAttempt, setEngineAttempt] = useState(0);
	const [load, setLoad] = useState<DocumentLoadState>(() =>
		Option.isSome(url) ? { status: "loading" } : { status: "idle" },
	);
	const [pageCount, setPageCount] = useState(0);
	const [page, setPage] = useState(1);
	const [scale, setScale] = useState(1);
	const [zoom, setZoom] = useState<ZoomMode>(initialSession?.zoom ?? DEFAULT_SESSION.zoom);
	const [rotation, setRotation] = useState<Rotation>(
		initialSession?.rotation ?? DEFAULT_SESSION.rotation,
	);
	const [layers] = useState(createLayerStore);
	const [find, setFind] = useState(NO_FIND);
	const [pdfDocument, setPdfDocument] = useState<Option<PDFDocumentProxy>>(undefined);

	const latest = useRef({ initialSession, onDocumentLoad, refreshUrl, url });
	useEffect(() => {
		latest.current = { initialSession, onDocumentLoad, refreshUrl, url };
	});
	const pageRef = useRef(1);
	/**
	 * A page moved to by command stays current until the scroll moves, even when the scroll can't
	 * bring its top past the current-page line, as near the end of a zoomed-out document.
	 */
	const heldPage = useRef<Option<{ page: number; scrollTop: number }>>(undefined);
	const hosts = useRef(new Map<number, HTMLDivElement>());
	const pendingScroll = useRef<Option<PendingScroll>>(undefined);
	const autoScrolling = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const passwordCallback = useRef<Option<(password: string) => void>>(undefined);
	/** The URL the last refresh handed over, so it gets no refresh of its own when it fails too. */
	const refreshedUrl = useRef<Option<string>>(undefined);
	/** Whether the viewer's pages belong to the document on screen, not one being replaced. */
	const documentShown = useRef(false);

	useEffect(() => {
		if (!container || !pagesRoot) return;
		let disposed = false;
		let created: Option<Engine>;
		loadPdfjs().then(
			(pdfjs) => {
				if (disposed) return;
				created = createEngine(pdfjs, container, pagesRoot);
				setEngine(created);
			},
			() => {
				if (!disposed) setLoad({ status: "failed" });
			},
		);
		return () => {
			disposed = true;
			if (created) releaseDocument(created);
			setEngine(undefined);
		};
	}, [container, pagesRoot, engineAttempt]);

	/**
	 * `if-needed` scrolls only when the rects aren't fully visible; `place` always puts them in the
	 * top third, for a page that hasn't rendered yet and may still change size.
	 */
	const scrollWithin = useCallback(
		(
			viewer: PDFViewer,
			scrollContainer: HTMLElement,
			request: PendingScroll,
			mode: "if-needed" | "place" | "refine",
		) => {
			const view = pageViewOf(viewer, request.page);
			const { viewBox, rotation: pageRotation } = view?.viewport ?? {};
			const box =
				viewBox && pageRotation !== undefined
					? unionBox(request.rects.map((rect) => toPageBox(rect, viewBox, pageRotation).box))
					: undefined;
			if (!view || !box) return;
			const contentBox = contentBoxOf(scrollContainer, view.div, box);
			const scrollView = {
				scrollLeft: scrollContainer.scrollLeft,
				scrollTop: scrollContainer.scrollTop,
				width: scrollContainer.clientWidth,
				height: scrollContainer.clientHeight,
			};
			const behavior = prefersReducedMotion() ? "auto" : "smooth";
			if (mode === "refine") {
				const target = topThirdTarget(contentBox, scrollView);
				if (
					Math.abs(target.top - (request.placedTop ?? scrollView.scrollTop)) > REFINE_TOLERANCE_PX
				)
					scrollContainer.scrollTo({ ...target, behavior });
				return;
			}
			const target =
				mode === "place"
					? topThirdTarget(contentBox, scrollView)
					: autoScrollTarget(contentBox, scrollView);
			if (target) {
				clearTimeout(autoScrolling.current);
				autoScrolling.current = setTimeout(() => {
					autoScrolling.current = undefined;
				}, SCROLL_SETTLE_MS);
				scrollContainer.scrollTo({ ...target, behavior });
			}
			request.placedTop = target?.top;
			request.onScrolled(Boolean(target));
		},
		[],
	);

	// Mirrors the page, zoom and rotation into state, and shows the document once pdf.js has
	// laid its pages out.
	useEffect(() => {
		if (!engine || !container) return;
		const { eventBus, viewer } = engine;
		const abort = new AbortController();
		const options = { signal: abort.signal };

		const currentPage = () => {
			const held = heldPage.current;
			if (held && held.scrollTop === container.scrollTop) return held.page;
			heldPage.current = undefined;
			return currentPageOf(viewer, container);
		};
		const trackPage = () => {
			const current = currentPage();
			if (current === pageRef.current) return;
			pageRef.current = current;
			setPage(current);
		};

		eventBus.on(
			"pagesinit",
			() => {
				const initial = latest.current.initialSession;
				viewer.pagesRotation = initial?.rotation ?? DEFAULT_SESSION.rotation;
				viewer.currentScaleValue = toScaleValue(initial?.zoom ?? DEFAULT_SESSION.zoom);
				const requested = Math.round(initial?.page ?? DEFAULT_SESSION.page);
				const start = clamp(requested, 1, viewer.pagesCount);
				if (start > 1) {
					viewer.currentPageNumber = start;
					heldPage.current = { page: start, scrollTop: container.scrollTop };
				}
				documentShown.current = true;
				trackPage();
				setPageCount(viewer.pagesCount);
				setLoad({ status: "ready" });
			},
			options,
		);
		eventBus.on(
			"scalechanging",
			({ scale: nextScale, presetValue }: ScaleChangingEvent) => {
				setScale(nextScale);
				setZoom(toZoomMode(presetValue, nextScale));
			},
			options,
		);
		eventBus.on(
			"rotationchanging",
			({ pagesRotation }: { pagesRotation: number }) => setRotation(toRotation(pagesRotation)),
			options,
		);
		eventBus.on("updateviewarea", trackPage, options);
		return () => abort.abort();
	}, [engine, container]);

	// Keeps a highlight host in each rendered page. A zoom or rotation resets every page, which
	// removes children pdf.js doesn't own; both events fire after the reset and before the next
	// paint, so highlights never blink out.
	useEffect(() => {
		if (!engine) return;
		const { eventBus, viewer } = engine;
		const abort = new AbortController();
		const options = { signal: abort.signal };
		const attach = (pageNumbers: readonly number[]) =>
			layers.attach(hostLayers(viewer, hosts.current, pageNumbers));
		const reattach = () => attach([...hosts.current.keys()]);
		eventBus.on(
			"pagerendered",
			({ pageNumber }: { pageNumber: number }) => attach([pageNumber]),
			options,
		);
		eventBus.on("scalechanging", reattach, options);
		eventBus.on("rotationchanging", reattach, options);
		return () => abort.abort();
	}, [engine, layers]);

	// Refines a scroll aimed at a page that hadn't rendered yet, and lets any scroll the reader
	// starts cancel one in flight.
	useEffect(() => {
		if (!engine || !container) return;
		const { eventBus, viewer } = engine;
		const abort = new AbortController();
		eventBus.on(
			"pagerendered",
			({ pageNumber }: { pageNumber: number }) => {
				const pending = pendingScroll.current;
				if (pending?.page !== pageNumber) return;
				pendingScroll.current = undefined;
				scrollWithin(viewer, container, pending, "refine");
			},
			{ signal: abort.signal },
		);
		const stopAutoScroll = () => {
			pendingScroll.current = undefined;
			if (autoScrolling.current === undefined) return;
			clearTimeout(autoScrolling.current);
			autoScrolling.current = undefined;
			container.scrollTo({ left: container.scrollLeft, top: container.scrollTop });
		};
		for (const type of ["wheel", "pointerdown", "touchstart", "keydown"] as const)
			container.addEventListener(type, stopAutoScroll, { passive: true, signal: abort.signal });
		return () => abort.abort();
	}, [engine, container, scrollWithin]);

	// Mirrors find's match count and whether it is still searching.
	useEffect(() => {
		if (!engine) return;
		const { eventBus, components } = engine;
		const abort = new AbortController();
		const options = { signal: abort.signal };
		eventBus.on(
			"updatefindmatchescount",
			({ matchesCount }: { matchesCount: MatchesCount }) =>
				setFind((current) => ({ ...current, ...matchesCount })),
			options,
		);
		eventBus.on(
			"updatefindcontrolstate",
			({ state, matchesCount }: { state: number; matchesCount: MatchesCount }) =>
				setFind({ ...matchesCount, pending: state === components.FindState.PENDING }),
			options,
		);
		return () => abort.abort();
	}, [engine]);

	// Refits a fit-mode zoom when the container resizes, at most once a frame. A hidden viewer
	// measures 0 × 0, which would fit the pages to a negative scale.
	useEffect(() => {
		if (!engine || !container || typeof ResizeObserver === "undefined") return;
		const { viewer } = engine;
		let frame: number | undefined;
		let fittedTo = { width: container.clientWidth, height: container.clientHeight };
		const observer = new ResizeObserver(() => {
			if (frame !== undefined) cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => {
				frame = undefined;
				const size = { width: container.clientWidth, height: container.clientHeight };
				if (size.width === 0 || size.height === 0) return;
				if (size.width === fittedTo.width && size.height === fittedTo.height) return;
				fittedTo = size;
				// pdf.js keeps the preset name while a fit mode is on, and a number otherwise.
				const mode = toZoomMode(viewer.currentScaleValue, viewer.currentScale);
				if (documentShown.current && isFitMode(mode)) viewer.currentScaleValue = mode;
			});
		});
		observer.observe(container);
		return () => {
			observer.disconnect();
			if (frame !== undefined) cancelAnimationFrame(frame);
		};
	}, [engine, container]);

	/**
	 * Fetches a fresh URL after `failedUrl` failed to load. A new URL reloads through `url`; the
	 * same one back needs a reload of its own. A failed fetch fails the load.
	 */
	const renewUrl = useCallback((failedUrl: string) => {
		setLoad({ status: "loading" });
		void latest.current.refreshUrl().then((fresh) => {
			refreshedUrl.current = fresh;
			// The fresh URL, or another document, has already replaced the one that failed.
			if (latest.current.url !== failedUrl) return;
			if (Option.isNone(fresh)) setLoad({ status: "failed" });
			else if (fresh === failedUrl) setAttempt((current) => current + 1);
		});
	}, []);

	useEffect(() => {
		if (!engine) return;
		const { core, assets, worker, eventBus, viewer, linkService, findController } = engine;
		if (documentShown.current) eventBus.dispatch("findbarclose", { source: null });
		documentShown.current = false;
		hosts.current.clear();
		pendingScroll.current = undefined;
		heldPage.current = undefined;
		pageRef.current = 1;
		layers.clear();
		setFind(NO_FIND);
		setPage(1);
		setPageCount(0);
		setPdfDocument(undefined);
		if (Option.isNone(url)) {
			releaseDocument(engine);
			setLoad({ status: "idle" });
			return;
		}
		let active = true;
		setLoad({ status: "loading" });

		const task = core.getDocument({ url, worker, ...assets });
		task.onPassword = (update: (password: string) => void, reason: number) => {
			if (!active) return;
			passwordCallback.current = update;
			setLoad({
				status: "password",
				incorrect: reason === core.PasswordResponses.INCORRECT_PASSWORD,
				checking: false,
			});
		};
		task.promise.then(
			(pdf) => {
				if (!active) return;
				refreshedUrl.current = undefined;
				passwordCallback.current = undefined;
				viewer.setDocument(pdf);
				linkService.setDocument(pdf, null);
				findController.setDocument(pdf);
				setPdfDocument(pdf);
				latest.current.onDocumentLoad?.(pdf);
			},
			(error: unknown) => {
				if (!active) return;
				if (error instanceof core.InvalidPDFException) setLoad({ status: "unreadable" });
				else if (refreshedUrl.current === url) setLoad({ status: "failed" });
				else renewUrl(url);
			},
		);
		return () => {
			active = false;
			passwordCallback.current = undefined;
			void task.destroy();
		};
	}, [engine, url, attempt, renewUrl, layers]);

	const goToPage = useCallback(
		(target: number) => {
			if (!engine || !container || !documentShown.current) return;
			const next = clamp(Math.round(target), 1, engine.viewer.pagesCount);
			engine.viewer.currentPageNumber = next;
			heldPage.current = { page: next, scrollTop: container.scrollTop };
			pageRef.current = next;
			setPage(next);
		},
		[engine, container],
	);

	const setZoomMode = useCallback(
		(next: ZoomMode) => {
			if (engine && documentShown.current) engine.viewer.currentScaleValue = toScaleValue(next);
		},
		[engine],
	);

	const stepZoom = useCallback(
		(direction: 1 | -1) => {
			if (!engine || !documentShown.current) return;
			const next = nextZoomStep(engine.viewer.currentScale, direction);
			if (next !== undefined) engine.viewer.currentScaleValue = toScaleValue(next);
		},
		[engine],
	);

	const closeFind = useCallback(() => {
		engine?.eventBus.dispatch("findbarclose", { source: null });
		setFind(NO_FIND);
	}, [engine]);

	const runFind = useCallback(
		(query: string, { again = false, previous = false } = {}) => {
			if (!engine) return;
			if (query.trim() === "") return closeFind();
			engine.eventBus.dispatch("find", {
				source: null,
				type: again ? "again" : "",
				query,
				caseSensitive: false,
				entireWord: false,
				highlightAll: true,
				findPrevious: previous,
				matchDiacritics: false,
			});
		},
		[engine, closeFind],
	);

	/**
	 * Scrolls the rects into view unless they already show, returning whether it could try: it
	 * can't before the document is on screen. A page that hasn't rendered yet is placed from its
	 * layout size, then refined once it renders.
	 */
	const scrollToRects = useCallback(
		(targetPage: number, rects: PdfRect[], onScrolled: (moved: boolean) => void): boolean => {
			if (!engine || !container || !documentShown.current) return false;
			const view = pageViewOf(engine.viewer, targetPage);
			if (!view) return false;
			const request = { page: targetPage, rects, onScrolled };
			const rendered = view.renderingState === engine.components.RenderingStates.FINISHED;
			pendingScroll.current = rendered ? undefined : request;
			scrollWithin(engine.viewer, container, request, rendered ? "if-needed" : "place");
			return true;
		},
		[engine, container, scrollWithin],
	);

	/** Turns every page a quarter turn, clockwise for `1`. The file never changes. */
	const rotate = useCallback(
		(direction: 1 | -1) => {
			if (!engine || !documentShown.current) return;
			engine.viewer.pagesRotation = toRotation(engine.viewer.pagesRotation + direction * 90);
		},
		[engine],
	);

	const print = useCallback(async () => {
		if (Option.isSome(pdfDocument)) await printDocument(pdfDocument);
	}, [pdfDocument]);

	const submitPassword = useCallback((password: string) => {
		const update = passwordCallback.current;
		if (!update) return;
		setLoad({ status: "password", incorrect: false, checking: true });
		update(password);
	}, []);

	/** Loads a fresh URL, since the one that failed may have expired. */
	const retry = useCallback(() => {
		if (!engine) {
			setLoad({ status: "loading" });
			setEngineAttempt((current) => current + 1);
		} else if (Option.isSome(url)) renewUrl(url);
	}, [engine, url, renewUrl]);

	return {
		containerRef: setContainer,
		pagesRef: setPagesRoot,
		load,
		pageCount,
		page,
		scale,
		zoom,
		rotation,
		/** Each rendered page's highlight layer; read it with `useSyncExternalStore`. */
		layers,
		find,
		pdfDocument,
		goToPage,
		rotate,
		print,
		setZoom: setZoomMode,
		stepZoom,
		runFind,
		closeFind,
		scrollToRects,
		submitPassword,
		retry,
	};
};

export type DocumentViewerController = ReturnType<typeof useDocumentViewer>;
